import React, { useEffect, useState } from 'react';
import { IconSearch as Search } from '@tabler/icons-react';
import { input } from '@/components/ui/design';

export const tableWrap = 'overflow-x-auto rounded-[24px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10';
export const th = 'px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-grey40 dark:text-nightMuted whitespace-nowrap';
export const td = 'px-4 py-3 text-sm text-grey10 dark:text-white align-top';
export const rowBorder = 'border-t border-black/[0.06] dark:border-white/10';
export const smallBtn = 'inline-flex h-9 items-center justify-center rounded-full px-4 text-xs font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]';
export const pill = 'inline-flex items-center rounded-full bg-grey99 px-2.5 py-1 text-xs font-semibold text-grey30 dark:bg-white/[0.06] dark:text-nightTextSecondary';

export const useDebounced = <T,>(value: T, ms = 300) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(timer);
    }, [value, ms]);
    return debounced;
};

export const SearchBox: React.FC<{ value: string; onChange: (value: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => (
    <div className="relative w-full md:max-w-[360px]">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-grey60 dark:text-nightMuted" />
        <input
            type="text"
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={`${input} pl-11`}
        />
    </div>
);

export const Pager: React.FC<{ page: number; limit: number; total: number; onPage: (page: number) => void }> = ({ page, limit, total, onPage }) => {
    const pages = Math.max(1, Math.ceil(total / limit));
    return (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm text-grey40 dark:text-nightMuted">
            <span>{total.toLocaleString()} total</span>
            <div className="flex items-center gap-2">
                <button type="button" className={smallBtn} disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
                <span>Page {page} of {pages}</span>
                <button type="button" className={smallBtn} disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
            </div>
        </div>
    );
};

export const EmptyRow: React.FC<{ colSpan: number; loading: boolean; text: string }> = ({ colSpan, loading, text }) => (
    <tr className={rowBorder}>
        <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-grey40 dark:text-nightMuted">{loading ? 'Loading...' : text}</td>
    </tr>
);

export const formatDate = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const money = (value: number) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const fullName = (user: { firstName?: string; lastName?: string }) => [user.firstName, user.lastName].filter(Boolean).join(' ');
